import { useNavigate, useLocation } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import { Card } from "@/components/ui/card";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

const AccountPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const accountId = auth.getAccountId();

  const {
    data: account,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetAccountByIdQuery({ accountId: accountId! }, { skip: !accountId });

  const handleSuccess = () => {
    toast.success("הפרופיל עודכן בהצלחה");
    // Navigate back if we came from another page
    const from = (location.state as { from?: string })?.from;
    if (from && from !== "/account") {
      navigate(from, { replace: true });
    }
  };

  const handleError = (error: unknown) => {
    console.error("Error updating profile:", error);
    toast.error(error instanceof Error ? error.message : "שגיאה בעדכון הפרופיל");
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="size-8" />
          <p className="text-muted-foreground">טוען...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <p className="text-destructive text-lg font-semibold">שגיאה בטעינת נתוני החשבון</p>
          <p className="text-muted-foreground">
            {error && typeof error === "object" && "message" in error ? String(error.message) : "אירעה שגיאה לא צפויה"}
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess && account) {
    const initialValues = {
      phoneNumber: account.accountInformation?.phoneNumber || "",
      businessName: account.accountInformation?.businessName || "",
      businessNumber: account.accountInformation?.businessNumber || "",
      city: account.accountInformation?.city || "",
    };

    // Check if user was redirected due to profile enforcement
    const wasEnforced = location.state?.enforced === true;

    return (
      <div className="container mx-auto p-6" dir="rtl">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="flex justify-center">
              <div className="p-3 rounded-full bg-chen-100">
                <Building2 className="w-8 h-8 text-chen-900" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-chen-900">הגדרות חשבון</h1>
            <p className="text-muted-foreground">נהלו את פרטי הפרופיל העסקי שלכם</p>
          </div>

          {/* Enforcement Notification Banner */}
          {wasEnforced && (
            <div className="bg-chen-100 border border-chen-900 rounded-lg p-6 text-center">
              <h2 className="text-chen-900 font-bold text-lg">השלמת הפרופיל נדרשת</h2>
              <p className="text-chen-900 mt-2">על מנת להמשיך להשתמש במערכת, אנא השלימו את פרטי החשבון</p>
            </div>
          )}

          {/* Profile Form Card */}
          <Card className="p-8">
            <ProfileForm
              initialValues={initialValues}
              onSuccess={handleSuccess}
              onError={handleError}
              submitButtonText="שמור שינויים"
            />
          </Card>
        </div>
      </div>
    );
  }

  return null;
};

export default AccountPage;
